import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
} from "@material-ui/core";
import { Collections, UserProfile } from "api";
import React, { useCallback, useMemo, useState } from "react";
import { useFirestore } from "react-redux-firebase";
import MaterialTable from "material-table";
import { LocalizationPTBr } from "util/MaterialTableUtil";

interface SelectUsersProps {
    open: boolean;
    onClose?: () => void;
    onSelectUser: (driver?: UserProfile) => void;
    filterOptions?: ((user: UserProfile) => boolean)[];
}

interface LazyFindUsersOptions {
    userName?: string;
    iRacingId?: number;
}

const useLazyFindUsers = () => {
    let firestore = useFirestore();
    return useCallback(
        (options: LazyFindUsersOptions) => {
            if (options.iRacingId) {
                return firestore
                    .collection(Collections.USERS)
                    .where("driverInfo.iracingId", "==", options.iRacingId)
                    .get();
            }

            return firestore
                .collection(Collections.USERS)
                .orderBy("displayName", "asc")
                .startAt(options.userName ? options.userName : "^")
                .endAt(`${options.userName ? options.userName : "^"}\uf8ff`)
                .get();
        },
        [firestore]
    );
};

const SelectUsers: React.FC<SelectUsersProps> = ({
    open,
    onClose,
    onSelectUser,
    filterOptions,
}) => {
    const [userName, setUserName] = useState("");
    const [iRacingId, setIRacingId] = useState<number>();
    const [users, setUsers] = useState<UserProfile[]>([]);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();
    const [loading, setLoading] = useState(false);
    const queryUsers = useLazyFindUsers();

    useMemo(() => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        setTimeoutId(
            setTimeout(() => {
                setLoading(true);
                queryUsers({ userName, iRacingId }).then((usersSnapshot) => {
                    const users = usersSnapshot.docs.map((userDoc) => {
                        return {
                            ...userDoc.data(),
                            id: userDoc.id,
                        } as UserProfile;
                    });
                    setUsers(users);
                    setLoading(false);
                    setTimeoutId(undefined);
                });
            }, 800)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryUsers, userName, iRacingId]);

    let usersOptions = useMemo(() => {
        let usersFiltered: UserProfile[] = [...users];
        if (filterOptions) {
            filterOptions.forEach((filter) => {
                usersFiltered = usersFiltered.filter(filter);
            });
        }
        usersFiltered.sort((d1, d2) =>
            d1.displayName < d2.displayName ? -1 : 1
        );
        return usersFiltered;
    }, [users, filterOptions]);

    // noinspection TypeScriptValidateTypes
    // @ts-ignore
    return (
        <Dialog
            open={open}
            disableEscapeKeyDown
            disableBackdropClick
            maxWidth="sm"
        >
            <DialogTitle>Adicionar Pilotos</DialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <TextField
                            label="Nome usuário"
                            value={userName}
                            onChange={({ currentTarget: { value } }) =>
                                setUserName(value)
                            }
                            autoFocus
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            type="number"
                            label="iRacing Id"
                            value={iRacingId}
                            onChange={({ currentTarget: { value } }) =>
                                setIRacingId(Number.parseInt(value))
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <MaterialTable
                            components={{ Container: Box }}
                            data={usersOptions}
                            isLoading={loading}
                            columns={[
                                {
                                    field: "displayName",
                                    title: "Nome",
                                    align: "center",
                                },
                                {
                                    field: "driverInfo.iracingId",
                                    title: "ID iRacing",
                                    align: "center",
                                },
                            ]}
                            options={{
                                draggable: false,
                                sorting: false,
                                toolbar: false,
                            }}
                            onRowClick={(event, user) => onSelectUser(user)}
                            localization={LocalizationPTBr}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    fullWidth={false}
                    onClick={() => {
                        if (onClose) {
                            onClose();
                        }
                    }}
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { SelectUsers };
